/**
 * The diner's cart, priced and placed through the one commerce client
 * (ORDR-42; the app's own Checkout since ORDR-166).
 *
 * PRICE. `priceCart` brings the server cart level with the diner's lines and
 * returns the SERVER QUOTE. `totalCents` is `quote.total_minor`, verbatim. No
 * figure is summed from prices here: this surface renders what the server
 * charges. A changed quantity replaces the server line.
 *
 * SUBTOTAL (ORDR-176). `subtotal_minor` is base prices only, so it is not shown.
 * The FOOD chain can put options, quantity, discounts, fees and a tip (after
 * tax) between the lines and `total_minor`. Discounts, fees and tip are named
 * as their own `charges`, from the quote's adjustments; the subtotal is what
 * remains of `total_minor` before tax and those charges. The shown lines
 * therefore add up to the total by construction, and a rule this client does
 * not name lands in the subtotal rather than going missing.
 *
 * MODE. The cart is opened with the mode the diner is placing for: the
 * resolver's, or the one chosen at Checkout. A different choice on an OPEN cart
 * PATCHes its `fulfilment_context` (COMM-127 allows it while OPEN, same firm),
 * and the quote must be taken again before the order can be placed, because the
 * total the diner saw was for the other mode. With no mode at all, no cart is
 * opened: the resolver never invents a mode, and neither does this.
 *
 * PAY. `placeOrder` is given `"pay-in-store"` or a card tokeniser. Pay in store
 * is handed NO tokeniser, so nothing on that path can tokenise. A card that
 * yields nothing posts nothing and says why.
 *
 * STALE QUOTE. A 409 re-quotes at once and holds placement until the diner has
 * seen the new total (`acknowledgeNewTotal`).
 *
 * PLACEMENT (ORDR-118). `placementGuard` is asked FIRST on every placement and,
 * when it names a reason, nothing is tokenised or posted. Pricing a cart is
 * browsing and is never guarded. The server refuses independently; this is
 * courtesy, not security.
 *
 * REFUSED (ORDR-219). A 403 on the cart create or a line add is commerce
 * refusing THIS SESSION (measured: AUTHZ-002, a session signed in to another
 * business). It is reported through `onCartRefused` so the diner is told why
 * and offered signing out, and still rejects, so nothing is priced or placed.
 */

import type { AuthoriseRequest, CartLine, PricedCart } from "../framework/types.js";
import { CardNotCaptured } from "./cardTokeniser.js";
import { cartSignature } from "./cartSignature.js";
import { CommerceRequestFailed } from "./commerceClient.js";
import type { CommerceClient, CommerceOrder, FieldErrors, Handover, Quote, QuoteAdjustment } from "./commerceClient.js";
import type { FulfilmentContext } from "./flowFromWire.js";
import { isOrderLine } from "../order/orderLine.js";

/** What Finix's hosted fields hand back. Never logged, never stored. */
export interface CardTokenisation {
  /** paio-commerce's `source_type`. Only the card path exists here. */
  readonly sourceType: "CARD";
  readonly token: string;
  readonly fraudSessionId: string;
}

/**
 * Filled by `cardTokeniser.ts` from payments-core's hosted Finix fields
 * (ORDR-55). Built only once a brand has refused pay in store (ORDR-166).
 */
export interface PaymentTokeniser {
  tokenise(request: AuthoriseRequest): Promise<CardTokenisation>;
}

export class ModeNotChosen extends Error {
  constructor() {
    super("Choose how you get your order before it can be priced.");
    this.name = "ModeNotChosen";
  }
}

export interface CustomerServicesDeps {
  readonly commerce: CommerceClient;
  readonly fulfilment: FulfilmentContext;
  readonly currency: string;
  /**
   * Asked before every placement. A string refuses placement with those words
   * and posts nothing; `null` lets it proceed.
   */
  readonly placementGuard?: () => string | null;
  /** Told of a 403 on the cart create or a line add (ORDR-219), with its reference. */
  readonly onCartRefused?: (correlationId: string) => void;
}

export interface PlaceOrderInput {
  readonly handover: Handover;
  readonly payment: "pay-in-store" | PaymentTokeniser;
}

export type PlaceOutcome =
  | { readonly kind: "placed"; readonly order: CommerceOrder }
  | { readonly kind: "pay-in-store-not-enabled" }
  | { readonly kind: "invalid"; readonly fieldErrors: FieldErrors; readonly correlationId: string }
  | { readonly kind: "stale-quote"; readonly quote: Quote }
  /** Nothing was posted, for the reason given in the diner's words. */
  | { readonly kind: "refused"; readonly message: string };

/** A charge the diner sees on its own line between Subtotal and Tax. */
export interface QuoteCharge {
  readonly label: "Discounts" | "Fees" | "Tip";
  readonly amountCents: number;
}

/** The framework's priced cart, plus the charges that make its lines add up. */
export interface QuotedCart extends PricedCart {
  /** Only the non-zero ones, in display order. */
  readonly charges: readonly QuoteCharge[];
}

export interface CustomerServices {
  priceCart(lines: readonly CartLine[]): Promise<QuotedCart>;
  /** The diner's mode; the cart follows it. */
  chooseMode(mode: string): Promise<void>;
  placeOrder(input: PlaceOrderInput): Promise<PlaceOutcome>;
  /** The diner has seen the re-quoted total; placement may be tried again. */
  acknowledgeNewTotal(): void;
}

const NEW_TOTAL_NOT_SEEN = "The price of your order changed. Check the new total, then place the order. Nothing has been charged.";
const NOT_PRICED = "Your total has to be worked out again for how you get your order. Check it, then place the order. Nothing has been charged.";

/** Placement is never attempted for a card the tokeniser did not capture. */
const CARD_REQUEST = { wallet: "card", answers: {}, path: "checkout" } as unknown as AuthoriseRequest;

const CHARGES: ReadonlyArray<{ label: QuoteCharge["label"]; matches: (adjustment: QuoteAdjustment) => boolean }> = [
  { label: "Discounts", matches: (adjustment) => adjustment.funding !== null },
  { label: "Fees", matches: (adjustment) => adjustment.rule_id === "food_fees" },
  { label: "Tip", matches: (adjustment) => adjustment.rule_id === "food_tip" },
];

function chargesOf(quote: Quote): QuoteCharge[] {
  const adjustments = quote.adjustments ?? [];
  return CHARGES.map(({ label, matches }) => ({
    label,
    amountCents: adjustments.filter(matches).reduce((sum, adjustment) => sum + adjustment.amount_minor, 0),
  })).filter((charge) => charge.amountCents !== 0);
}

export function pricedFromQuote(quote: Quote, lines: readonly CartLine[]): QuotedCart {
  const nameByOffer = new Map(lines.map((line) => [line.menuItemId, line.name]));
  const charges = chargesOf(quote);
  const charged = charges.reduce((sum, charge) => sum + charge.amountCents, 0);
  return {
    cartSignature: cartSignature(lines),
    lines: quote.lines.map((line) => ({
      lineId: line.line_id,
      description: nameByOffer.get(line.offer_ref) ?? line.offer_ref,
      amountCents: line.line_total_minor,
    })),
    subtotalCents: quote.total_minor - quote.tax_minor - charged,
    charges,
    taxCents: quote.tax_minor,
    totalCents: quote.total_minor,
    currency: quote.currency,
  };
}

export function createCustomerServices(deps: CustomerServicesDeps): CustomerServices {
  let cartId: string | null = null;
  let mode = deps.fulfilment.fulfilment_mode;
  /** Diner line handle to the server line and the quantity it was sent with. */
  const serverLines = new Map<string, { lineId: string; quantity: number }>();
  let latestQuote: Quote | null = null;
  let awaitingAcknowledgement = false;

  function context(): FulfilmentContext {
    return { ...deps.fulfilment, fulfilment_mode: mode };
  }

  /**
   * One cart call at a time. The Menu prices the cart for "View cart" and the
   * Cart prices it again; two overlapping syncs would both see a line as unsent
   * and add it twice.
   */
  let queue: Promise<unknown> = Promise.resolve();
  function serial<T>(work: () => Promise<T>): Promise<T> {
    const run = queue.then(work);
    queue = run.catch(() => undefined);
    return run;
  }

  /** A cart write, with a 403 reported as a refusal before it rejects. */
  async function write<T>(call: () => Promise<T>): Promise<T> {
    try {
      return await call();
    } catch (error) {
      if (error instanceof CommerceRequestFailed && error.status === 403) {
        deps.onCartRefused?.(error.correlationId);
      }
      throw error;
    }
  }

  async function syncAndQuote(lines: readonly CartLine[]): Promise<QuotedCart> {
    if (cartId === null) {
      if (mode === null) {
        throw new ModeNotChosen();
      }
      cartId = await write(() => deps.commerce.openCart(deps.currency, context()));
    }
    const id = cartId;
    const wanted = new Map(lines.map((line) => [line.id, line.quantity]));
    for (const [handle, sent] of [...serverLines]) {
      if (wanted.get(handle) !== sent.quantity) {
        await deps.commerce.removeLine(id, sent.lineId);
        serverLines.delete(handle);
      }
    }
    for (const line of lines) {
      if (!serverLines.has(line.id)) {
        // An app line carries its size and its selection tree (ORDR-165); the
        // note is composed into the order's `kitchen_note` at Checkout.
        const configuration = isOrderLine(line)
          ? { portion_code: line.portionCode, selections: line.wireSelections }
          : undefined;
        const lineId = await write(() => deps.commerce.addLine(id, line.menuItemId, line.quantity, configuration));
        serverLines.set(line.id, { lineId, quantity: line.quantity });
      }
    }
    const quote = await deps.commerce.quote(id);
    latestQuote = quote;
    return pricedFromQuote(quote, lines);
  }

  async function changeMode(next: string): Promise<void> {
    if (next === mode) {
      return;
    }
    mode = next;
    if (cartId !== null) {
      await deps.commerce.setFulfilment(cartId, context());
      // The total the diner saw was for the other mode.
      latestQuote = null;
    }
  }

  async function place({ handover, payment }: PlaceOrderInput): Promise<PlaceOutcome> {
    const refusal = deps.placementGuard?.() ?? null;
    if (refusal !== null) {
      return { kind: "refused", message: refusal };
    }
    if (awaitingAcknowledgement) {
      return { kind: "refused", message: NEW_TOTAL_NOT_SEEN };
    }
    if (cartId === null || latestQuote === null) {
      return { kind: "refused", message: NOT_PRICED };
    }
    let card: CardTokenisation | null = null;
    if (payment !== "pay-in-store") {
      try {
        card = await payment.tokenise(CARD_REQUEST);
      } catch (error) {
        if (error instanceof CardNotCaptured) {
          return { kind: "refused", message: error.message };
        }
        throw error;
      }
    }
    const outcome = await deps.commerce.checkout({
      cartId,
      quoteVersion: latestQuote.quote_version,
      payment:
        card === null
          ? { method: "PAY_IN_STORE" }
          : { method: "CARD", sourceType: card.sourceType, token: card.token, fraudSessionId: card.fraudSessionId },
      handover,
    });
    if (outcome.kind === "stale-quote") {
      const fresh = await deps.commerce.quote(cartId);
      latestQuote = fresh;
      awaitingAcknowledgement = true;
      return { kind: "stale-quote", quote: fresh };
    }
    return outcome;
  }

  return {
    priceCart: (lines) => serial(() => syncAndQuote(lines)),
    chooseMode: (next) => serial(() => changeMode(next)),
    placeOrder: (input) => serial(() => place(input)),
    acknowledgeNewTotal() {
      awaitingAcknowledgement = false;
    },
  };
}
